import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import NewsletterModal from '../home/components/NewsletterModal';
import CampaignMiniBanner from '../../components/feature/CampaignMiniBanner';

export default function RentalService() {
  const [scrolled, setScrolled] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const plans = [
    {
      name: 'トライアルプラン',
      duration: '1ヶ月',
      description: '購入前のお試しに最適',
      features: ['1製品からレンタル可能', '配送・設置込み', '期間終了後の購入割引'],
    },
    {
      name: 'スタンダードプラン',
      duration: '3ヶ月〜',
      description: 'じっくり試したい方に',
      features: ['複数製品のセットレンタル', '配送・設置・回収込み', 'メンテナンスサポート', '購入時のレンタル料一部充当'],
    },
    {
      name: '法人プラン',
      duration: 'カスタム',
      description: 'オフィス・イベント向け',
      features: ['大量導入対応', 'カスタム期間設定', '専任サポート', 'イベント短期利用可'],
    },
  ];

  const steps = [
    { icon: 'ri-search-line', title: '製品を選ぶ', desc: 'お好みのMeVER製品を選択' },
    { icon: 'ri-calendar-line', title: '期間を決める', desc: 'ご希望のレンタル期間を設定' },
    { icon: 'ri-truck-line', title: 'お届け・設置', desc: 'ご指定の場所へ配送・設置' },
    { icon: 'ri-heart-line', title: '体験する', desc: 'じっくりとMeVERを体験' },
  ];

  return (
    <div className="relative">
      <Navbar scrolled={scrolled} />
      <div className="pt-16 sm:pt-20">
        <CampaignMiniBanner />
      </div>
      
      {/* Hero */}
      <section className="pt-8 sm:pt-12 pb-12 sm:pb-20 bg-gradient-to-b from-gray-900 to-black" aria-labelledby="rental-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <Link to="/" className="inline-flex items-center text-white/60 hover:text-white mb-6 sm:mb-8 transition-colors cursor-pointer tracking-wide text-sm sm:text-base">
            <i className="ri-arrow-left-line mr-2" aria-hidden="true"></i>
            トップに戻る
          </Link>
          
          {/* Price Revision Notice */}
          <Link
            to="/campaign"
            className="block mb-6 sm:mb-8 cursor-pointer group"
            aria-label="価格改定のお知らせ - キャンペーンページへ"
          >
            <div className="bg-gradient-to-r from-red-900/40 via-red-800/40 to-red-900/40 border border-red-700/30 rounded-lg sm:rounded-xl p-4 sm:p-5 backdrop-blur-sm hover:border-red-600/50 transition-all">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 sm:gap-4 flex-1">
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 bg-red-500 text-white text-xs font-medium tracking-wide rounded-md">
                      <i className="ri-error-warning-line text-sm" aria-hidden="true"></i>
                      重要
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm sm:text-base font-light tracking-wide mb-0.5">
                      2026年4月1日より価格改定
                    </p>
                    <p className="text-white/70 text-xs sm:text-sm font-extralight tracking-wide">
                      3/31まで価格維持予約受付中 + Amazonギフト券5,000円分プレゼント
                    </p>
                  </div>
                </div>
                <i className="ri-arrow-right-line text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all text-lg flex-shrink-0" aria-hidden="true"></i>
              </div>
            </div>
          </Link>

          <div className="text-center max-w-4xl mx-auto">
            <span className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-[#8E7AB5]/20 text-[#8E7AB5] text-xs sm:text-sm tracking-[0.1em] rounded-full mb-4 sm:mb-6">
              2026年夏 サービス開始予定
            </span>
            <h1 id="rental-title" className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-extralight text-white mb-6 sm:mb-8 tracking-[0.1em] sm:tracking-[0.15em]">
              MeVER Rental
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl font-extralight text-white/80 mb-4 sm:mb-6 tracking-wide">
              購入前に、ご自宅やオフィスでじっくり体験
            </p>
            <p className="text-sm sm:text-base md:text-lg font-extralight text-white/60 leading-relaxed mb-8 sm:mb-12 max-w-2xl mx-auto tracking-wide">
              MeVERの製品を実際の環境でお試しいただけるレンタルサービス。<br className="hidden sm:block" />
              購入を迷っている方も、まずは体験から始められます。
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-[#8E7AB5] text-white text-sm sm:text-base font-light tracking-[0.1em] hover:bg-[#7A6A9E] transition-colors whitespace-nowrap rounded-md cursor-pointer"
              aria-haspopup="dialog"
            >
              サービス開始通知を受け取る
            </button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-20 md:py-24 bg-white" aria-labelledby="steps-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 id="steps-heading" className="text-2xl sm:text-3xl md:text-4xl font-extralight text-black mb-10 sm:mb-12 md:mb-16 text-center tracking-[0.1em] sm:tracking-[0.15em]">ご利用の流れ</h2>
          <ol className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {steps.map((step, index) => (
              <li key={index} className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-[#8E7AB5]/10 rounded-full mx-auto mb-4 sm:mb-6">
                  <i className={`${step.icon} text-2xl sm:text-3xl text-[#8E7AB5]`} aria-hidden="true"></i>
                </div>
                <div className="text-xs sm:text-sm text-[#8E7AB5] font-light mb-1.5 sm:mb-2 tracking-[0.1em] sm:tracking-[0.15em]">STEP {index + 1}</div>
                <h3 className="text-base sm:text-lg md:text-xl font-light text-black mb-1.5 sm:mb-2 tracking-wide">{step.title}</h3>
                <p className="text-xs sm:text-sm md:text-base text-black/60 font-extralight tracking-wide">{step.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 sm:py-20 md:py-24 bg-gray-50" aria-labelledby="plans-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 id="plans-heading" className="text-2xl sm:text-3xl md:text-4xl font-extralight text-black mb-3 sm:mb-4 text-center tracking-[0.1em] sm:tracking-[0.15em]">プラン（予定）</h2>
          <p className="text-sm sm:text-base text-black/60 text-center mb-10 sm:mb-12 md:mb-16 font-extralight tracking-wide">※ 料金・詳細はサービス開始時に発表予定</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8" role="list">
            {plans.map((plan, index) => (
              <article key={index} className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 hover:shadow-xl transition-shadow" role="listitem">
                <h3 className="text-lg sm:text-xl md:text-2xl font-light text-black mb-1.5 sm:mb-2 tracking-wide">{plan.name}</h3>
                <p className="text-sm sm:text-base text-[#8E7AB5] font-light mb-3 sm:mb-4 tracking-wide">{plan.duration}</p>
                <p className="text-sm sm:text-base text-black/60 font-extralight mb-4 sm:mb-6 tracking-wide">{plan.description}</p>
                <ul className="space-y-2 sm:space-y-3" aria-label={`${plan.name}の特徴`}>
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 sm:gap-3">
                      <i className="ri-check-line text-[#8E7AB5] mt-0.5" aria-hidden="true"></i>
                      <span className="text-sm sm:text-base text-black/80 font-extralight tracking-wide">{feature}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Target */}
      <section className="py-16 sm:py-20 md:py-24 bg-white" aria-labelledby="target-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 id="target-heading" className="text-2xl sm:text-3xl md:text-4xl font-extralight text-black mb-10 sm:mb-12 md:mb-16 text-center tracking-[0.1em] sm:tracking-[0.15em]">こんな方におすすめ</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8" role="list">
            {[
              { icon: 'ri-user-line', title: '購入前に試したい方', desc: '高額な買い物だからこそ、まずは体験から' },
              { icon: 'ri-building-line', title: 'オフィス導入検討中の法人', desc: '社員の反応を見てから本導入を決定' },
              { icon: 'ri-calendar-event-line', title: 'イベント利用', desc: '展示会やポップアップでの短期利用' },
              { icon: 'ri-home-line', title: '引越し予定の方', desc: '新居での使用感を確認してから購入' },
            ].map((item, index) => (
              <article key={index} className="text-center p-4 sm:p-6" role="listitem">
                <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3 sm:mb-4">
                  <i className={`${item.icon} text-xl sm:text-2xl text-black/60`} aria-hidden="true"></i>
                </div>
                <h3 className="text-sm sm:text-base md:text-lg font-light text-black mb-1.5 sm:mb-2 tracking-wide">{item.title}</h3>
                <p className="text-xs sm:text-sm text-black/60 font-extralight tracking-wide">{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 md:py-24 bg-black" aria-labelledby="rental-cta-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 id="rental-cta-heading" className="text-2xl sm:text-3xl md:text-4xl font-extralight text-white mb-4 sm:mb-6 tracking-[0.1em] sm:tracking-[0.15em]">サービス開始をお知らせします</h2>
          <p className="text-sm sm:text-base text-white/60 font-extralight mb-8 sm:mb-10 tracking-wide">
            メールアドレスをご登録いただくと、サービス開始時に優先的にご案内いたします。
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-[#8E7AB5] text-white text-sm sm:text-base font-light tracking-[0.1em] hover:bg-[#7A6A9E] transition-colors whitespace-nowrap rounded-md cursor-pointer"
            aria-haspopup="dialog"
          >
            通知を受け取る
          </button>
        </div>
      </section>

      <Footer />

      {showModal && (
        <NewsletterModal
          title="MeVER Rental 開始情報"
          description="レンタルサービス開始時にお知らせいたします"
          formType="rental"
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
